import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { SchoolInfo } from "@/types";
import { formatRank, formatScore, reportTitle, type ClassReport } from "@/services/gradeService";
import { loadPdfImage, pdfDate } from "./pdfImages";
import { drawSchoolHeader, finalY, GREEN, LIGHT, MUTED, TEXT } from "./reportCardPdf";

/** Colonnes facultatives de la liste par ordre de mérite (rang, élève, moyenne et mention sont toujours là). */
export interface MeritListOptions {
  matricule: boolean;
  gender: boolean;
  birthDate: boolean;
  totalPoints: boolean;
  /** La note de chaque matière : la page passe en paysage. */
  subjects: boolean;
  remark: boolean;
  /** Colonne vide pour la signature des parents. */
  signature: boolean;
}

export const MERIT_OPTION_LABELS: Record<keyof MeritListOptions, string> = {
  matricule: "Matricule",
  gender: "Sexe",
  birthDate: "Date de naissance",
  totalPoints: "Total des points",
  subjects: "Note de chaque matière (page en paysage)",
  remark: "Appréciation générale (trimestre)",
  signature: "Signature des parents (colonne vide)",
};

const SMALL_WORDS = new Set(["de", "la", "le", "les", "et", "du", "des", "l", "d", "à", "en"]);

/**
 * Nom court d'une matière pour une colonne étroite : « Éducation physique et sportive » → « EPS »,
 * « Histoire-Géographie » → « Hist.-Géo. », « Mathématiques » → « Mathémat. ».
 */
export function abbreviate(name: string): string {
  const words = name.split(/[\s'’]+/).filter((w) => w && !SMALL_WORDS.has(w.toLowerCase()));
  if (words.length >= 3) return words.map((w) => w.charAt(0).normalize("NFD").charAt(0).toUpperCase()).join("");
  if (name.includes("-")) return name.split("-").map((part) => (part.length > 5 ? `${part.slice(0, 4)}.` : part)).join("-");
  return name.length > 10 ? `${name.slice(0, 8)}.` : name;
}

/** Liste de la classe par ordre de mérite, pour une évaluation ou pour le trimestre. */
export async function exportMeritListPdf(report: ClassReport, school: SchoolInfo, options: MeritListOptions): Promise<void> {
  const landscape = options.subjects;
  const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "mm", format: "a4" });
  const pageW = landscape ? 297 : 210;
  const pageH = landscape ? 210 : 297;
  const margin = 12;
  const logo = await loadPdfImage(school.logo);
  drawSchoolHeader(doc, school, logo, [`Année scolaire ${report.period.schoolYear}`, `Classe : ${report.className}`], pageW, margin);

  doc.setTextColor(...TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("LISTE PAR ORDRE DE MÉRITE", pageW / 2, 42, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`${report.className} — ${reportTitle(report)}`, pageW / 2, 48, { align: "center" });

  const ranked = [...report.students].sort(
    (a, b) => (a.rank ?? 9999) - (b.rank ?? 9999) || a.lastName.localeCompare(b.lastName, "fr"),
  );
  const columns: Array<{ head: string; value: (i: number) => string; align?: "left" | "right" | "center"; bold?: boolean }> = [
    { head: "Rang", value: (i) => formatRank(ranked[i].rank, ranked[i].rankTied, ranked[i].gender), align: "center", bold: true },
    ...(options.matricule ? [{ head: "Matricule", value: (i: number) => ranked[i].matricule }] : []),
    { head: "Élève", value: (i) => `${ranked[i].lastName.toUpperCase()} ${ranked[i].firstName}` },
    ...(options.gender ? [{ head: "Sexe", value: (i: number) => ranked[i].gender ?? "", align: "center" as const }] : []),
    ...(options.birthDate ? [{ head: "Né(e) le", value: (i: number) => (ranked[i].birthDate ? pdfDate(ranked[i].birthDate) : "") }] : []),
    ...(options.subjects
      ? report.subjects.map((subject) => ({
          head: abbreviate(subject.name),
          value: (i: number) => formatScore(ranked[i].subjects.find((s) => s.subjectId === subject.subjectId)?.average ?? null),
          align: "right" as const,
        }))
      : []),
    ...(options.totalPoints ? [{ head: "Points", value: (i: number) => formatScore(ranked[i].totalPoints), align: "right" as const }] : []),
    { head: `Moyenne / ${report.gradeScale}`, value: (i) => formatScore(ranked[i].average), align: "right", bold: true },
    { head: "Mention", value: (i) => ranked[i].mention ?? "—" },
    ...(options.remark && report.scope === "PERIOD" ? [{ head: "Appréciation", value: (i: number) => ranked[i].remark ?? "" }] : []),
    ...(options.signature ? [{ head: "Signature des parents", value: () => "" }] : []),
  ];

  autoTable(doc, {
    startY: 53,
    margin: { left: margin, right: margin, bottom: 18 },
    head: [columns.map((c) => c.head)],
    body: ranked.map((_, i) => columns.map((c) => c.value(i))),
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: options.subjects ? 7.5 : 8.5,
      cellPadding: options.signature ? 2.6 : options.subjects ? 1.4 : 1.8,
      overflow: "linebreak",
      textColor: TEXT,
      lineColor: [210, 214, 208],
      lineWidth: 0.2,
      valign: "middle",
    },
    headStyles: { fillColor: GREEN, textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
    alternateRowStyles: { fillColor: [248, 250, 248] },
    columnStyles: Object.fromEntries(
      columns.map((c, i) => [
        i,
        {
          halign: c.align ?? "left",
          fontStyle: c.bold ? ("bold" as const) : ("normal" as const),
          ...(c.head === "Signature des parents" ? { cellWidth: 38 } : {}),
          ...(c.head === "Mention" ? { minCellWidth: 22 } : {}),
          ...(c.head === "Élève" ? { minCellWidth: 36 } : {}),
          ...(c.head === "Matricule" ? { minCellWidth: 22 } : {}),
          ...(c.head === "Rang" ? { minCellWidth: 13 } : {}),
        },
      ]),
    ),
  });

  // Légende des matières abrégées, puis statistiques de la classe.
  let y = finalY(doc, 200) + 5;
  const legend = options.subjects
    ? report.subjects.filter((s) => abbreviate(s.name) !== s.name).map((s) => `${abbreviate(s.name)} : ${s.name}`)
    : [];
  if (legend.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    const lines = doc.splitTextToSize(legend.join("  ·  "), pageW - margin * 2) as string[];
    doc.text(lines, margin, y);
    y += lines.length * 3.5 + 2;
  }
  if (y > pageH - 30) {
    doc.addPage();
    y = 20;
  }
  doc.setFillColor(...LIGHT);
  doc.roundedRect(margin, y, pageW - margin * 2, 12, 2, 2, "F");
  doc.setFontSize(8.5);
  doc.setTextColor(...TEXT);
  doc.text(
    [
      `Effectif : ${report.stats.count}`,
      `Classés : ${report.stats.graded}`,
      `Moyenne de la classe : ${formatScore(report.stats.average)}`,
      `Plus forte : ${formatScore(report.stats.highest)}`,
      `Plus faible : ${formatScore(report.stats.lowest)}`,
      `Au-dessus de ${formatScore(report.passingAverage)} : ${report.stats.passed}`,
    ].join("   ·   "),
    pageW / 2,
    y + 7.5,
    { align: "center" },
  );

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(`Édité le ${pdfDate(new Date().toISOString())} — ${school.name} — page ${p}/${pages}`, pageW / 2, pageH - 8, { align: "center" });
  }
  doc.save(`Merite-${report.className}-${reportTitle(report)}.pdf`.replace(/[\s'’/—]+/g, "-").replace(/-+/g, "-"));
}
